var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import debug from "debug";
import { Util } from "@auvious/common";
import { AUVIOUS_VALID_URL_REGEX, WIDGET_REFERRER_ID, WIDGET_CONVERSATION_TYPE, WIDGET_MODE, OriginModeEnum, WIDGET_COBROWSE_REQUEST_PREFIX, ErrorCode, } from "./models";
import { codeError, timeout } from "./utils";
import { sessionStore } from "@auvious/utils";
import { Restorable } from "./models/Restorable";
const log = debug("auvious:integrations");
/**
 * tutorial on webChat V2
 * https://developer.mypurecloud.com/api/webchat/widget-version2.html#code_sample_for_web_page_with_genesys_webchat_widget
 *
 * webChatService API
 * https://docs.genesys.com/Documentation/GWC/latest/WidgetsAPI/WebChatService
 */
export class GenesysChatPremiseService extends Restorable {
    /**
     * GenesysChatPremiseService constructor.
     *
     * pcEnvironment used only for selecting mirror the Genesys Widgets library.
     * dataUrl dataURL used instead of cometD parameters, (short polling mode)
     * cometURL cometURL to connect to
     * cometChannel channel to connect to
     * cometApiUrl api url, used for file download/upload
     */
    constructor(config, handlers) {
        super();
        this.config = config;
        this.handlers = handlers;
        this.attachmentConstraints = {
            mimeType: ".bmp,.csv,.doc,.docx,.gif,.htm,.jpg,.log,.pdf,.png,.ppt,.pptx,.tif,.txt,.xls,.xlsx",
            fileSize: 0,
            friendlyFileSize: null,
        };
        // we use these flags to call started,ended handlers only once
        this.startedHandlerCalled = false;
        this.endedHandlerCalled = false;
        this.agentConnectedCalled = false;
        this.agentNotifiedCalled = false;
        this.resolvedPlugin = new Promise((resolve) => (this.resolvePlugin = resolve));
        this.debugSent = false;
        if (config.customDomain) {
            this.customDomain = new RegExp(
            // escape any special characters in the stringToFind to prevent them from being interpreted as regex operators.
            config.customDomain.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i");
        }
        // create script
        let scriptSrc;
        let pluginsPath;
        if (this.config.pcWidgetScriptUrl) {
            scriptSrc = this.config.pcWidgetScriptUrl;
            pluginsPath = this.config.pcWidgetPluginsPath;
        }
        else {
            const widgetBaseUrl = `https://apps.${this.config.pcEnvironment}/widgets/9.0/`;
            scriptSrc = widgetBaseUrl + "cxbus.min.js";
            pluginsPath = widgetBaseUrl + "plugins/";
        }
        const widgetScriptElement = document.createElement("script");
        widgetScriptElement.setAttribute("src", scriptSrc);
        // on load
        widgetScriptElement.addEventListener("load", () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            try {
                globalThis.CXBus.configure({
                    debug: false,
                    pluginsPath,
                });
                this.loadConfig();
                yield globalThis.CXBus.loadPlugin("widgets-core");
                this._plugin = globalThis.CXBus.registerPlugin("auvious");
                this.subscribeToEvents();
                this.resolvePlugin(this._plugin);
                (_b = (_a = this.handlers).ready) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.INIT_CHAT_SERVICE_FAILED));
            }
        }), { once: true });
        document.head.appendChild(widgetScriptElement);
    }
    downloadFile(file) {
        return new Promise((resolve, reject) => {
            this._plugin
                .command("WebChatService.downloadFile", {
                fileId: file.id,
                fileName: file.name,
            })
                .done((e) => {
                log("downloadFile done", e);
                resolve();
            })
                .fail((e) => {
                log("downloadFile failed", e);
                reject(e);
            });
        });
    }
    loadConfig() {
        if (globalThis._genesys) {
            // do not override externally set _genesys config
            log("genesys configured externally", globalThis._genesys);
            return;
        }
        if (!this.config.cometUrl && this.config.dataUrl) {
            globalThis._genesys = {
                widgets: {
                    main: {
                        downloadGoogleFont: false,
                    },
                    webchat: {
                        dataURL: this.config.dataUrl,
                    },
                },
            };
        }
        else {
            globalThis._genesys = {
                widgets: {
                    main: {
                        downloadGoogleFont: false,
                    },
                    webchat: {
                        cometD: {
                            enabled: true,
                            cometURL: this.config.cometUrl,
                            channel: this.config.cometChannel,
                            apiURL: this.config.cometApiUrl,
                        },
                    },
                },
            };
        }
    }
    subscribeToEvents() {
        this._plugin.subscribe("WebChatService.ready", (e) => {
            var _a, _b;
            log("WebChatService.ready", e);
            (_b = (_a = this.handlers) === null || _a === void 0 ? void 0 : _a.connected) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
        this._plugin.subscribe("WebChatService.messageReceived", (e) => {
            try {
                log("WebChatService.messageReceived", e);
                let originalMessage;
                if (e.data.messages.length) {
                    e.data.messages.forEach((message) => {
                        var _a, _b, _c;
                        switch (message.type) {
                            case "PushUrl":
                                this.handlePushUrl(message);
                                break;
                            case "Message":
                                originalMessage = (_a = e.data.originalMessages) === null || _a === void 0 ? void 0 : _a.find((m) => m.index === message.index);
                                switch (message.from.type) {
                                    case "Bot":
                                        this.handleBotMessages(message, originalMessage);
                                        break;
                                    case "Agent":
                                        !!originalMessage && "eventAttributes" in originalMessage
                                            ? this.handleBotMessages(message, originalMessage)
                                            : this.handleNormalMessages(message);
                                        break;
                                    default:
                                        this.handleNormalMessages(message);
                                        break;
                                }
                                break;
                            case "FileUploaded":
                                this.handleFileMessages(message);
                                break;
                            case "IdleClose":
                                this.handleNoticeMessages(message, "idle");
                                break;
                            // these are handled by explicit events
                            case "ParticipantJoined":
                                if (((_b = message.from) === null || _b === void 0 ? void 0 : _b.type) === "Agent")
                                    this.handleNoticeMessages(message, "participant-joined");
                                break;
                            case "ParticipantLeft":
                                if (((_c = message.from) === null || _c === void 0 ? void 0 : _c.type) === "Agent")
                                    this.handleNoticeMessages(message, "participant-left");
                                break;
                            // case "TypingStarted":
                            //   if (message.from?.type === "Agent")
                            //     this.handlers.agentTypingStarted?.();
                            //   break;
                            // case "TypingStopped":
                            //   if (message.from?.type === "Agent")
                            //     this.handlers.agentTypingStopped?.();
                            //   break;
                        }
                    });
                }
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.CONVERSATION_MESSAGE_PARSE_FAILED));
            }
        });
        this._plugin.subscribe("WebChatService.error", (e) => {
            this.handlers.error(codeError(e, ErrorCode.GENESYS_API_ERROR));
        });
        this._plugin.subscribe("WebChatService.started", (e) => {
            var _a, _b, _c, _d;
            try {
                log("webChatService: started", e);
                const data = e.data.data;
                const conversation = {
                    uri: data === null || data === void 0 ? void 0 : data.WebSocketUrl,
                    id: data === null || data === void 0 ? void 0 : data.conversationId,
                    jwt: data === null || data === void 0 ? void 0 : data.jwt,
                    memberId: (data === null || data === void 0 ? void 0 : data.memberId) || "unknown",
                };
                // on premise we rely on the clientConnected to get the proper data
                if (!this.startedHandlerCalled && !this.isOnPremise) {
                    // save conv because on refresh we don't get the jwt and uri
                    sessionStore.setItem(conversation.id, JSON.stringify(conversation));
                    this.startedHandlerCalled = true;
                    (_b = (_a = this.handlers).started) === null || _b === void 0 ? void 0 : _b.call(_a, conversation);
                }
            }
            catch (ex) {
                log(ex);
                this.handlers.error(codeError(ex, ErrorCode.CONVERSATION_START_FAILED));
            }
            this.agentNotifiedCalled = true;
            (_d = (_c = this.handlers).agentNotified) === null || _d === void 0 ? void 0 : _d.call(_c);
        });
        this._plugin.subscribe("WebChatService.ended", () => {
            var _a, _b, _c;
            log("WebChatService.ended");
            sessionStore.removeItem((_a = this.$conversation) === null || _a === void 0 ? void 0 : _a.id);
            // todo: delete this.conversationsMap[]?
            if (!this.endedHandlerCalled) {
                this.endedHandlerCalled = true;
                (_c = (_b = this.handlers).ended) === null || _c === void 0 ? void 0 : _c.call(_b);
            }
            this.agentConnectedCalled = false;
        });
        this._plugin.subscribe("WebChatService.disconnected", () => {
            var _a, _b;
            log("WebChatService.disconnected");
            (_b = (_a = this.handlers).disconnected) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
        this._plugin.subscribe("WebChatService.reconnected", () => {
            var _a, _b;
            log("WebChatService.reconnected");
            (_b = (_a = this.handlers).reconnected) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
        this._plugin.subscribe("WebChatService.agentConnected", (e) => {
            var _a, _b, _c, _d;
            log("WebChatService.agentConnected", e);
            const member = this.parseEventToMember(e);
            if (member) {
                (_b = (_a = this.handlers).agentJoined) === null || _b === void 0 ? void 0 : _b.call(_a, member);
            }
            if (this.heldTicket) {
                (_d = (_c = this.handlers).roomCreated) === null || _d === void 0 ? void 0 : _d.call(_c, this.heldTicket);
                this.heldTicket = null;
            }
            this.agentConnectedCalled = true;
        });
        this._plugin.subscribe("WebChatService.agentDisconnected", (e) => {
            var _a, _b;
            log("WebChatService.agentDisconnected", e);
            const member = this.parseEventToMember(e);
            (_b = (_a = this.handlers).agentLeft) === null || _b === void 0 ? void 0 : _b.call(_a, member);
        });
        this._plugin.subscribe("WebChatService.agentTypingStarted", (e) => {
            var _a, _b;
            log("WebChatService.agentTypingStarted", e);
            (_b = (_a = this.handlers).agentTypingStarted) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
        this._plugin.subscribe("WebChatService.agentTypingStopped", (e) => {
            var _a, _b;
            log("WebChatService.agentTypingStopped", e);
            (_b = (_a = this.handlers).agentTypingStopped) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
        this._plugin.subscribe("WebChatService.clientConnected", (e) => {
            var _a, _b, _c, _d, _e, _f, _g;
            try {
                log("WebChatService.clientConnected", e);
                // START: Genesys Cloud
                const conversationId = (_a = e.data.message.conversation) === null || _a === void 0 ? void 0 : _a.id;
                let conv;
                if (conversationId) {
                    try {
                        const convStr = sessionStore.getItem(conversationId);
                        conv = JSON.parse(convStr);
                    }
                    catch (ex) {
                        log("WebChatService.clientConnected - Could not parse conversation");
                    }
                    // coming from page refresh. get as much data as we can
                    if (!this.startedHandlerCalled) {
                        this.startedHandlerCalled = true;
                        (_c = (_b = this.handlers).started) === null || _c === void 0 ? void 0 : _c.call(_b, {
                            uri: conv === null || conv === void 0 ? void 0 : conv.uri,
                            id: conversationId,
                            jwt: conv === null || conv === void 0 ? void 0 : conv.jwt,
                            memberId: e.data.message.sender.id,
                        });
                    }
                }
                // START: Genesys Engage
                if (!conversationId &&
                    e.data.message.from &&
                    !this.startedHandlerCalled) {
                    if (!this.agentNotifiedCalled) {
                        this.agentNotifiedCalled = true;
                        (_e = (_d = this.handlers).agentNotified) === null || _e === void 0 ? void 0 : _e.call(_d);
                    }
                    this.startedHandlerCalled = true;
                    (_g = (_f = this.handlers).started) === null || _g === void 0 ? void 0 : _g.call(_f, {
                        uri: null,
                        id: "conversationId",
                        jwt: null,
                        memberId: e.data.message.from.id,
                    });
                }
            }
            catch (ex) {
                this.handlers.error(codeError(ex, ErrorCode.CONVERSATION_CUSTOMER_CONNECT_FAILED));
            }
        });
        this._plugin.subscribe("WebChatService.clientDisconnected", (e) => {
            var _a, _b;
            log("WebChatService.clientDisconnected", e);
            (_b = (_a = this.handlers).customerLeft) === null || _b === void 0 ? void 0 : _b.call(_a);
        });
    }
    get isOnPremise() {
        return this.config.chatMode === "genesys-premise";
    }
    // private handleParticipantJoined(e: any) {
    //     const messages = e.data.messages.filter(m => m.type === 'ParticipantJoined');
    //     // todo: maybe do something?
    // }
    handlePushUrl(message) {
        var _a, _b, _c, _d, _e;
        if (AUVIOUS_VALID_URL_REGEX.test(message.text) ||
            ((_a = this.customDomain) === null || _a === void 0 ? void 0 : _a.test(message.text))) {
            if (this.isOnPremise && !this.agentConnectedCalled) {
                this.heldTicket = message.text;
            }
            else {
                (_c = (_b = this.handlers).roomCreated) === null || _c === void 0 ? void 0 : _c.call(_b, message.text);
            }
        }
        else {
            (_e = (_d = this.handlers).messageReceived) === null || _e === void 0 ? void 0 : _e.call(_d, {
                id: message.id || message.index,
                index: message.index,
                text: message.text,
                type: "text",
                isLocal: message.from.type === "Client",
                timestamp: new Date(message.timestamp || message.utcTime),
                status: "ack",
                senderId: message.from.id || message.from.participantId,
                senderName: message.from.name || message.from.nickname,
            });
        }
    }
    handleBotMessages(message, originalMessage) {
        var _a, _b;
        if (!message.text) {
            return;
        }
        const quickReplies = [];
        if (originalMessage) {
            try {
                if (originalMessage.eventAttributes["structured-content"]["genesys-chat"]) {
                    const reply = JSON.parse(originalMessage.eventAttributes["structured-content"]["genesys-chat"].content);
                    if (reply.contentType === "quick-replies") {
                        reply.content.forEach((r) => {
                            quickReplies.push({
                                action: r.action,
                                payload: r.text,
                                text: r.text,
                            });
                        });
                    }
                }
            }
            catch (ex) {
                log(ex);
            }
        }
        (_b = (_a = this.handlers).messageReceived) === null || _b === void 0 ? void 0 : _b.call(_a, {
            id: message.id || message.index,
            index: message.index,
            text: message.text,
            type: "bot",
            isLocal: message.from.type === "Client",
            timestamp: new Date(message.timestamp || message.utcTime),
            status: "ack",
            senderId: message.from.id || message.from.participantId,
            senderName: message.from.name || message.from.nickname,
            quickReplies,
        });
    }
    handleNoticeMessages(message, noticeType) {
        var _a, _b, _c, _d, _e, _f, _g;
        (_b = (_a = this.handlers).messageReceived) === null || _b === void 0 ? void 0 : _b.call(_a, {
            id: message.id || message.index,
            index: message.index,
            text: message.text,
            type: "notice",
            noticeType,
            isLocal: ((_c = message.from) === null || _c === void 0 ? void 0 : _c.type) === "Client",
            timestamp: new Date(message.timestamp || message.utcTime),
            status: "ack",
            senderId: ((_d = message.from) === null || _d === void 0 ? void 0 : _d.id) || ((_e = message.from) === null || _e === void 0 ? void 0 : _e.participantId),
            senderName: ((_f = message.from) === null || _f === void 0 ? void 0 : _f.name) || ((_g = message.from) === null || _g === void 0 ? void 0 : _g.nickname),
        });
    }
    handleNormalMessages(message) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!message.text) {
            return;
        }
        if (AUVIOUS_VALID_URL_REGEX.test(message.text) ||
            ((_a = this.customDomain) === null || _a === void 0 ? void 0 : _a.test(message.text))) {
            log("webChatService: room created", message.text);
            // discard room created, we will get it from 'PushUrl'
            if (!this.isOnPremise) {
                (_c = (_b = this.handlers).roomCreated) === null || _c === void 0 ? void 0 : _c.call(_b, message.text);
            }
        }
        else if (message.text.indexOf(WIDGET_COBROWSE_REQUEST_PREFIX) > -1) {
            log("CXOne: cobrowse requested", message.text);
            // auvious:cobrowse-request:<request-type>:<ticket>
            const request = message.text.split(":");
            const requestType = request[2];
            const ticket = request[3];
            (_e = (_d = this.handlers).cobrowseRequested) === null || _e === void 0 ? void 0 : _e.call(_d, ticket, requestType);
        }
        else {
            (_g = (_f = this.handlers).messageReceived) === null || _g === void 0 ? void 0 : _g.call(_f, {
                id: message.id || message.index,
                index: message.index,
                text: message.text,
                type: "text",
                isLocal: message.from.type === "Client",
                timestamp: new Date(message.timestamp || message.utcTime),
                status: "ack",
                senderId: message.from.id || message.from.participantId,
                senderName: message.from.name || message.from.nickname,
            });
        }
    }
    handleFileMessages(message) {
        var _a, _b;
        (_b = (_a = this.handlers).messageReceived) === null || _b === void 0 ? void 0 : _b.call(_a, {
            id: message.id || message.index,
            index: message.index,
            file: {
                id: message.fileDetails["file-id"],
                name: message.fileDetails["file-name"],
                size: message.fileDetails["file-size"],
            },
            type: "file",
            isLocal: message.from.type === "Client",
            timestamp: new Date(message.timestamp),
            status: "ack",
            senderId: message.from.id || message.from.nickname,
            senderName: message.from.name || message.from.nickname,
        });
    }
    parseEventToMember(e) {
        var _a, _b, _c, _d, _e, _f, _g;
        try {
            const message = e.data.message || e.data.messages[0];
            if (!message)
                return null;
            // Genesys Cloud or Engage
            const memberId = ((_a = message.sender) === null || _a === void 0 ? void 0 : _a.id) || message.id || ((_b = message.from) === null || _b === void 0 ? void 0 : _b.id);
            return {
                id: memberId || message.id || message.index,
                // TODO: solve inconsistency
                // @ts-ignore
                role: (_d = (message.role || ((_c = message.from) === null || _c === void 0 ? void 0 : _c.type))) === null || _d === void 0 ? void 0 : _d.toUpperCase(),
                displayName: ((_f = (_e = e.data.agents) === null || _e === void 0 ? void 0 : _e[memberId]) === null || _f === void 0 ? void 0 : _f.name) || ((_g = message.from) === null || _g === void 0 ? void 0 : _g.name),
                avatarImageUrl: message.avatarImageUrl,
                customFields: null,
                joinDate: message.timestamp,
                state: message.state,
            };
        }
        catch (ex) {
            this.handlers.error(codeError(ex, ErrorCode.CONVERSATION_MESSAGE_PARSE_FAILED));
        }
    }
    reconnect(contact, conversationType) {
        return __awaiter(this, void 0, void 0, function* () {
            return undefined;
        });
    }
    createChat(contact, conversationType) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const form = {
                firstname: contact.firstName,
                lastname: contact.lastName || "unknown",
                email: contact.email,
                subject: contact.subject,
                userData: Object.assign(Object.assign({}, contact), { [WIDGET_REFERRER_ID]: window.origin, [WIDGET_CONVERSATION_TYPE]: conversationType, [WIDGET_MODE]: this.config.kioskMode
                        ? OriginModeEnum.kiosk
                        : OriginModeEnum.website }),
            };
            (_b = (_a = this.handlers).starting) === null || _b === void 0 ? void 0 : _b.call(_a);
            const plugin = yield Promise.race([
                this.resolvedPlugin,
                timeout(25000),
            ]);
            (_d = (_c = this.handlers) === null || _c === void 0 ? void 0 : _c.connecting) === null || _d === void 0 ? void 0 : _d.call(_c);
            return new Promise((resolve, reject) => {
                if (plugin) {
                    plugin
                        .command("WebChatService.startChat", form)
                        .done(resolve)
                        .fail(reject);
                }
                else {
                    reject(codeError(new Error("PluginLoadTimeout"), ErrorCode.INIT_CHAT_SERVICE_FAILED));
                }
            });
        });
    }
    endChat() {
        return new Promise((resolve, reject) => {
            this._plugin.command("WebChatService.endChat").done(resolve).fail(reject);
        });
    }
    acknowledgeMessages() {
        return __awaiter(this, void 0, void 0, function* () { });
    }
    sendMessage(message, bodyType = "standard", metadata) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const msg = {
                id: Util.uuidgen(),
                text: message,
                type: "text",
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: (_a = this.$conversation) === null || _a === void 0 ? void 0 : _a.memberId,
            };
            (_c = (_b = this.handlers).messageSent) === null || _c === void 0 ? void 0 : _c.call(_b, Object.assign(Object.assign({}, msg), { metadata }));
            let cmd;
            let options;
            switch (bodyType) {
                case "notice":
                    cmd = "WebChatService.sendFilteredMessage";
                    options = {
                        message: `notice: ${message}`,
                        regex: /^notice:/,
                    };
                    break;
                default:
                    cmd = "WebChatService.sendMessage";
                    options = { message };
                    break;
            }
            return new Promise((resolve, reject) => {
                this._plugin
                    .command(cmd, options)
                    .done(resolve)
                    .fail((err) => {
                    msg.status = "error";
                    this.handlers.messageError(msg);
                    reject(err);
                });
            });
        });
    }
    sendTyping() {
        return new Promise((resolve, reject) => {
            this._plugin
                .command("WebChatService.sendTyping")
                .done(resolve)
                .fail(reject);
        });
    }
    sendFile(fileInputRef) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            // send placeholder message to show spinner for uploading
            const file = (_a = fileInputRef.files) === null || _a === void 0 ? void 0 : _a.item(0);
            const msg = {
                id: Util.uuidgen(),
                text: "",
                file: {
                    id: null,
                    name: file.name,
                    size: file.size,
                },
                type: "file",
                isLocal: true,
                timestamp: new Date(),
                status: "sent",
                senderId: (_b = this.$conversation) === null || _b === void 0 ? void 0 : _b.memberId,
            };
            (_d = (_c = this.handlers).messageSent) === null || _d === void 0 ? void 0 : _d.call(_c, msg);
            // https://help.mypurecloud.com/articles/send-media-and-files-in-chat/
            return new Promise((resolve, reject) => {
                this._plugin
                    .command("WebChatService.sendFile", { files: fileInputRef })
                    .done(resolve)
                    .fail((e) => {
                    msg.status = "error";
                    this.handlers.messageError(msg);
                    reject(e);
                });
            });
        });
    }
    registerTypingInput(inputRef) {
        return new Promise((resolve, reject) => {
            this._plugin
                .command("WebChatService.registerTypingPreviewInput", {
                input: inputRef,
            })
                .done(resolve)
                .fail(reject);
        });
    }
    destroy() {
        this.startedHandlerCalled = false;
        this.endedHandlerCalled = false;
        this.agentConnectedCalled = false;
        this.agentNotifiedCalled = false;
    }
    customerConnected() {
        return __awaiter(this, void 0, void 0, function* () {
            // todo: implement
            return true;
        });
    }
}
